import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArchivedBadge, MissingBadge, Field, JiraLink, Kv, PageHeader, Pager, ProgressBar, SecretsTabs, SortTh, errMsg, fmtDate, fmtDateTime,
  invalidateFilters, useSecretFilters, useSecretsAccess, useUrlFilters,
} from './common';
import { BulkBar, KeyFilters, KeysTable, useKeyList } from './SecretKeysList';
import { EventList } from './SecretKeyDetail';
import { FolderOwnersPanel, OwnerEditForm } from '../repositories/RepoOwners';

const REPO_SORTS = [
  ['open', 'Open sign-offs'], ['keys', 'Keys'], ['progress', 'Progress'], ['present', 'Still in code'],
  ['partial', 'Partly cleaned'],
  ['critical', 'Critical keys'], ['revoked', 'Keys revoked'], ['slug', 'Name'], ['owner', 'Owner'], ['team', 'Team'],
  ['jira', 'Jira'], ['scanned', 'Last scan'],
];
const TEXT_SORTS = new Set(['slug', 'owner', 'team', 'jira']);

export function SecretReposList() {
  const { params, get, set } = useUrlFilters();
  const filters = useSecretFilters();
  const { can_edit: canEdit } = useSecretsAccess();
  const navigate = useNavigate();
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teamMsg, setTeamMsg] = useState('');
  const query = params.toString();

  const load = useCallback(() => {
    setLoading(true);
    const p = {};
    ['q', 'team', 'owner', 'owner_status', 'done', 'archived', 'sort', 'dir', 'page'].forEach((k) => { if (get(k)) p[k] = get(k); });
    p.limit = 100;
    axios.get('/secrets/repos', { params: p })
      .then((r) => setData(r.data)).catch((e) => setError(errMsg(e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  useEffect(() => { load(); }, [load]);

  const refreshTeams = () => {
    setTeamMsg('Reading the employee directory…');
    axios.post('/secrets/repos/refresh-teams')
      .then((r) => {
        setTeamMsg(`${r.data.updated} repos updated. ${r.data.owner_not_in_directory} owners are not in the active directory. ${r.data.kept_manual} teams set by hand were kept.`);
        invalidateFilters(); load();
      })
      .catch((e) => setTeamMsg(errMsg(e)));
  };

  const [archMsg, setArchMsg] = useState('');
  const syncArchived = () => {
    setArchMsg('Reading the Archive project on Bitbucket…');
    axios.post('/secrets/repos/sync-archived')
      .then((r) => {
        const d = r.data;
        setArchMsg(`${d.tracked_archived} tracked repos are archived. Newly archived: ${d.archived.length ? d.archived.join(', ') : 'none'}`
          + ` (${d.pairs_gone_not_fp} key-in-repo pairs now count as gone).`
          + (d.restored.length ? ` Moved out of the archive: ${d.restored.join(', ')}.` : '')
          + ` ${d.tracked_missing || 0} tracked repo${d.tracked_missing === 1 ? ' is' : 's are'} not on Bitbucket`
          + (d.missing?.length ? ` (new: ${d.missing.join(', ')}).` : '.')
          + (d.found_again?.length ? ` Back on Bitbucket: ${d.found_again.join(', ')}.` : ''));
        load();
      })
      .catch((e) => setArchMsg(errMsg(e)));
  };

  const sel = (key, label, options, width = 180) => (
    <div style={{ width }}>
      <label className="form-label">{label}</label>
      <select className="form-input" value={get(key)} onChange={(e) => set({ [key]: e.target.value })}>
        <option value="">Any</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  return (
    <div>
      <PageHeader title="Secrets rotation · Repos" />
      <div className="page-body">
        <SecretsTabs />
        <div className="card" style={{ padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ width: 220 }}>
              <label className="form-label">Search</label>
              <input className="form-input" defaultValue={get('q')} placeholder="Repo, owner or Jira key"
                onKeyDown={(e) => { if (e.key === 'Enter') set({ q: e.target.value.trim() }); }}
                onBlur={(e) => { if (e.target.value.trim() !== get('q')) set({ q: e.target.value.trim() }); }} />
            </div>
            {sel('team', 'Team', [...(filters?.teams || []).map((t) => [t, t]), ['Unknown', 'Unknown']])}
            {sel('owner', 'Owner', (filters?.owners || []).map((o) => [o.email, `${o.name || o.email} (${o.repos})`]), 220)}
            {sel('owner_status', 'Owner status', [['active', 'Active'], ['left', 'Likely left'], ['other', 'Ambiguous / verify']], 160)}
            {sel('done', 'Sign-off', [['yes', 'Every key ready'], ['no', 'Keys still open']], 150)}
            {sel('archived', 'Bitbucket', [['no', 'Active repos'], ['yes', 'Archived repos'], ['missing', 'Not on Bitbucket']], 150)}
            <div style={{ width: 160 }}>
              <label className="form-label">Sort</label>
              <select className="form-input" value={get('sort') || 'open'} onChange={(e) => set({ sort: e.target.value })}>
                {REPO_SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <button className="btn btn-ghost" onClick={() => set({ dir: get('dir') === 'asc' ? 'desc' : 'asc' })}>
              {get('dir') === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>
            <span style={{ flex: 1 }} />
            {canEdit && <button className="btn btn-secondary" onClick={syncArchived}
              title="Repos in Bitbucket's Archive project stop being scanned and their keys count as gone. The daily scan does this too.">
              Sync archived repos</button>}
            {canEdit && <button className="btn btn-secondary" onClick={refreshTeams}>Refresh teams from directory</button>}
          </div>
          {teamMsg && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>{teamMsg}</div>}
          {archMsg && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>{archMsg}</div>}
          <ArchiveSyncStatus st={data.archive_sync} onMissing={() => set({ archived: 'missing' })} />
        </div>
        {error && <div className="error-msg">{error}</div>}
        <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>{data.total} repos {loading && <span className="spinner" />}</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {[['slug', 'Repo'], ['owner', 'Owner'], ['team', 'Team'], ['keys', 'Keys'], ['progress', 'Signed off'],
                  ['present', 'Still in code'], ['partial', 'Partly cleaned'], ['critical', 'Critical · High'], ['revoked', 'Keys revoked'],
                  ['jira', 'Jira'], ['scanned', 'Last scan']].map(([k, l]) => (
                  <SortTh key={k} sortKey={k} label={l} get={get} set={set} defaultSort="open"
                    firstDir={TEXT_SORTS.has(k) ? 'asc' : 'desc'} />
                ))}
              </tr>
            </thead>
            <tbody>
              {data.data.map((r) => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/secrets/repos/${r.id}`)}>
                  <td>{r.repo_slug}
                    {r.folders > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }} title="Sub-folders with an owner of their own">{r.folders} folder owner{r.folders === 1 ? '' : 's'}</div>}
                    {r.archived_at && <div style={{ marginTop: 3 }}><ArchivedBadge at={r.archived_at} project={r.bitbucket_project} /></div>}
                    {r.missing_at && <div style={{ marginTop: 3 }}><MissingBadge at={r.missing_at} /></div>}</td>
                  <td style={{ fontSize: 12 }}>{r.owner_name || '—'}
                    <div style={{ color: 'var(--text-muted)' }}>{r.owner_status}</div></td>
                  <td style={{ fontSize: 12 }}>{r.team || <span style={{ color: 'var(--text-muted)' }}>Unknown</span>}
                    {r.team_source === 'manual' && <span style={{ color: 'var(--text-muted)' }}> (set by hand)</span>}</td>
                  <td>{r.keys_}</td>
                  <td><div style={{ fontSize: 12 }}>{r.ready} of {r.pairs}</div>
                    <ProgressBar value={r.ready} total={r.pairs} title={`${r.ready} of ${r.pairs} sign-offs marked ready to revoke${r.pairs !== r.keys_ ? ' (one per key per owned folder)' : ''}`} /></td>
                  <td style={{ fontSize: 12 }} title="False positives left out">{r.present} <span style={{ color: 'var(--text-muted)' }}>(was {r.in_code_at_import})</span>
                    {r.gone_archived > 0 && <div style={{ color: 'var(--text-muted)' }}>{r.gone_archived} gone by archiving</div>}</td>
                  <td style={{ fontSize: 12 }} onClick={(e) => e.stopPropagation()}>
                    {r.partial_keys ? (
                      <Link to={`/secrets/repos/${r.id}?progress=partial`}
                        title="Keys still in code here although some of their files are already clean">
                        {r.partial_keys} key{r.partial_keys === 1 ? '' : 's'}
                        <div style={{ color: 'var(--text-muted)' }}>
                          {r.partial_files_cleaned} of {r.partial_files_cleaned + r.partial_files_left} files cleaned
                        </div>
                      </Link>
                    ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>{r.critical} · {r.high}</td>
                  <td style={{ fontSize: 12 }}>{r.revoked}{r.eligible ? <span style={{ color: '#a5b4fc' }}> · {r.eligible} eligible</span> : ''}</td>
                  <td onClick={(e) => e.stopPropagation()}><JiraLink jiraKey={r.jira_key} base={filters?.jira_base_url} /></td>
                  <td style={{ fontSize: 12 }}>{r.last_scanned_at ? fmtDate(r.last_scanned_at) : '—'}
                    {r.archived_at && <div style={{ color: 'var(--text-muted)' }}>not scanned, archived</div>}
                    {!r.archived_at && r.missing_at && <div style={{ color: '#fca5a5' }}>not on Bitbucket</div>}
                    {!r.archived_at && !r.missing_at && r.last_scan_status && r.last_scan_status !== 'ok' &&
                      <div style={{ color: '#fbbf24' }}>{r.last_scan_status.replace('_', ' ')}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager page={data.page} limit={data.limit} total={data.total} onPage={(p) => set({ page: p }, { keepPage: true })} />
      </div>
    </div>
  );
}

// When the archive sync last ran, and a warning when its latest attempt failed:
// repos archived since the last good sync still count as in code until it works.
function ArchiveSyncStatus({ st, onMissing }) {
  if (!st) return null;
  const failing = !!st.last_error; // a successful sync clears it
  const missing = st.tracked_missing ? (
    <> {' '}<button className="btn btn-ghost" style={{ padding: 0, fontSize: 12 }} onClick={onMissing}>
      {st.tracked_missing} tracked repo{st.tracked_missing === 1 ? ' is' : 's are'} not on Bitbucket.</button></>
  ) : null;
  if (failing) {
    return (
      <div style={{ marginTop: 8, fontSize: 12, color: '#fbbf24' }}>
        The archive sync could not read Bitbucket at {fmtDateTime(st.last_error_at)} ({st.last_error}).
        {' '}Last successful sync: {st.last_ok_at ? fmtDateTime(st.last_ok_at) : 'never'}.
        {' '}Repos archived since then still count as in code; use Sync archived repos to retry.{missing}
      </div>
    );
  }
  if (!st.last_ok_at && !missing) return null;
  return (
    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
      {st.last_ok_at && <>Archived repos last synced from Bitbucket {fmtDateTime(st.last_ok_at)}.</>}{missing}
    </div>
  );
}

function RepoEdit({ repo, onSaved }) {
  const [jira, setJira] = useState(repo.jira_key || '');
  const [team, setTeam] = useState(repo.team || '');
  const [notes, setNotes] = useState(repo.notes || '');
  const [msg, setMsg] = useState('');
  useEffect(() => { setJira(repo.jira_key || ''); setTeam(repo.team || ''); setNotes(repo.notes || ''); }, [repo]);
  const save = () => {
    const body = { jira_key: jira, notes };
    if (team !== (repo.team || '')) body.team = team;
    axios.patch(`/secrets/repos/${repo.id}`, body)
      .then(() => { setMsg('Saved'); invalidateFilters(); onSaved(); })
      .catch((e) => setMsg(errMsg(e)));
  };
  return (
    <div className="card">
      <h3 style={{ fontSize: 15, marginBottom: 14 }}>Repo rotation</h3>
      <Field label="Jira card (repo level)">
        <input className="form-input" value={jira} placeholder="SEC-123 or a Jira link" onChange={(e) => setJira(e.target.value)} />
      </Field>
      <Field label="Team">
        <input className="form-input" value={team} onChange={(e) => setTeam(e.target.value)}
          placeholder="From the owner's department" />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          {repo.team_source === 'manual' ? 'Set by hand; the directory refresh keeps it.' :
            repo.team_lead ? `From the directory. Team lead: ${repo.team_lead}.` : 'Owner not found in the active directory.'}
        </div>
      </Field>
      <Field label="Notes">
        <textarea className="form-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <button className="btn btn-primary" onClick={save}>Save</button>
      {msg && <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</span>}
    </div>
  );
}

export function SecretRepoDetail() {
  const { id } = useParams();
  const filters = useSecretFilters();
  const { can_edit: canEdit } = useSecretsAccess();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [confirmedBy, setConfirmedBy] = useState('');
  const [signMsg, setSignMsg] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [own, setOwn] = useState(null);
  const [editOwner, setEditOwner] = useState(false);
  const [scope, setScope] = useState('');   // '' = every scope of the repo
  const { params, get, set } = useUrlFilters();
  const extra = { repo_id: id };
  const keys = useKeyList(get, params, extra);

  const load = useCallback(() => {
    axios.get(`/secrets/repos/${id}`).then((r) => {
      setD(r.data);
      return axios.get(`/repo-ownership/${encodeURIComponent(r.data.repo.repo_slug)}`).then((o) => setOwn(o.data))
        .catch(() => setOwn(null));
    }).catch((e) => setError(errMsg(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const reload = () => { load(); keys.load(); };

  const signoff = (status, onlyAbsent) => {
    const where = scope ? `in ${scopeLabel(scope)}` : 'in this repo';
    const what = status === 'open' ? `Re-open every key ${where}?` :
      onlyAbsent ? `Mark every key ${where} that is no longer in current code as ready to revoke?` :
        `Mark every key ${where} as ready to revoke?`;
    if (!window.confirm(what)) return;
    axios.post(`/secrets/repos/${id}/signoff`, { status, only_absent: !!onlyAbsent, confirmed_by: confirmedBy,
      scope_id: scope || undefined })
      .then((r) => { setSignMsg(`${r.data.changed} sign-offs updated`); reload(); })
      .catch((e) => setSignMsg(errMsg(e)));
  };
  const scopes = d?.scopes || [];
  const scopeLabel = (sid) => {
    const sc = scopes.find((x) => String(x.id) === String(sid));
    return sc ? (sc.folder_path ? `folder ${sc.folder_path}` : 'the repo root') : 'this scope';
  };

  if (error) return <div className="page-body"><div className="error-msg">{error}</div></div>;
  if (!d) return <div className="page-body"><span className="spinner" /></div>;
  const r = d.repo;
  const c = d.counts;
  const picked = scope ? scopes.find((x) => String(x.id) === scope) : null;
  const absentCount = picked ? picked.absent_not_signed : c.absent_not_signed;

  return (
    <div>
      <PageHeader title={`Repo · ${r.repo_slug}`}>
        Owner {r.owner_name || 'unknown'} ({r.owner_email || 'no email'}), {r.owner_status}.{' '}
        {r.owner_source === 'sheet' ? 'From the repo ownership sheet (live default reviewer in Bitbucket).'
          : r.owner_source === 'manual' ? 'Set by hand.'
            : 'Owner is the top human committer of the last 50 commits, an unreviewed guess.'}
        {scopes.length > 1 ? ` ${scopes.length - 1} folder${scopes.length === 2 ? ' has' : 's have'} an owner of ${scopes.length === 2 ? 'its' : 'their'} own; those keys are signed off by the folder owner.` : ''}
      </PageHeader>
      <div className="page-body">
        <SecretsTabs />
        {r.archived_at && (
          <div className="card" style={{ marginBottom: 16, borderColor: 'var(--border)', fontSize: 13 }}>
            <ArchivedBadge at={r.archived_at} project={r.bitbucket_project} />{' '}
            This repo is in Bitbucket project {r.bitbucket_project || 'Archive'} (found {fmtDateTime(r.archived_at)}).
            It is no longer scanned, and keys that were still in its code count as gone. They remain in its git history,
            so they still need signing off and revoking.
          </div>
        )}
        {r.missing_at && !r.archived_at && (
          <div className="card" style={{ marginBottom: 16, borderColor: 'rgba(239,68,68,0.4)', fontSize: 13 }}>
            <MissingBadge at={r.missing_at} />{' '}
            Bitbucket says this repo does not exist (first seen {fmtDateTime(r.missing_at)}, last checked {fmtDateTime(r.missing_checked_at)}).
            It was deleted, renamed, or is not visible to the app's Bitbucket account, so it cannot be scanned.
            Its keys still count as they did at the last successful scan ({r.last_scanned_at ? fmtDate(r.last_scanned_at) : 'never'}).
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div className="card">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
              <Kv label="Keys">{c.keys_}{c.pairs !== c.keys_ ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.pairs} sign-offs (one per key per owned folder)</div> : null}</Kv>
              <Kv label="Ready to revoke">{c.ready} of {c.pairs}</Kv>
              <Kv label="Still in code">{c.present} (was {c.in_code_at_import}){(c.present_fp || c.present_wf) ? `, ${c.present_fp || 0} false positives${c.present_wf ? `, ${c.present_wf} won't-fix` : ''} left out` : ''}</Kv>
              <Kv label="Gone but not signed off">{c.absent_not_signed}</Kv>
              <Kv label="Partly cleaned">
                {c.partial?.keys_ ? (
                  <button className="btn btn-ghost" style={{ padding: 0, textAlign: 'left' }} onClick={() => set({ progress: 'partial' })}
                    title="Show the keys still in code here although some of their files are already clean">
                    {c.partial.keys_} keys
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {c.partial.files_cleaned} of {c.partial.files_cleaned + c.partial.files_left} files cleaned
                    </div>
                  </button>
                ) : 'None'}
              </Kv>
              <Kv label="Team">{r.team || 'Unknown'}</Kv>
              <Kv label="Next contributor"><span style={{ fontSize: 12 }}>{r.next_contributor || '—'}</span></Kv>
              <Kv label="Last scan">{r.last_scanned_at ? fmtDateTime(r.last_scanned_at) : 'Not yet'} {r.last_scan_status && r.last_scan_status !== 'ok' ? `(${r.last_scan_status})` : ''}</Kv>
              <Kv label="Links">
                <a href={r.bitbucket_url} target="_blank" rel="noreferrer">Bitbucket</a>
                {r.confluence_url && <> · <a href={r.confluence_url} target="_blank" rel="noreferrer">Key removal plan</a></>}
              </Kv>
            </div>
            <div style={{ marginTop: 8 }}>
              <ProgressBar value={c.ready} total={c.pairs} title="Sign-offs marked ready to revoke" />
            </div>
            {canEdit && <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {scopes.length > 1 && (
                <select className="form-input" style={{ width: 220 }} value={scope} onChange={(e) => setScope(e.target.value)}
                  title="A folder owner signs off only their folder">
                  <option value="">All scopes (whole repo)</option>
                  {scopes.map((sc) => <option key={sc.id} value={sc.id}>{sc.folder_path ? `folder ${sc.folder_path}` : 'Repo root (repo owner)'}</option>)}
                </select>
              )}
              <input className="form-input" style={{ width: 200 }} placeholder={picked?.folder_path ? 'Confirmed by (folder owner)' : 'Confirmed by (repo owner)'}
                value={confirmedBy} onChange={(e) => setConfirmedBy(e.target.value)} />
              <button className="btn btn-secondary" onClick={() => signoff('ready_to_revoke', true)}
                disabled={!absentCount}>Mark keys gone from code ready ({absentCount})</button>
              <button className="btn btn-secondary" onClick={() => signoff('ready_to_revoke', false)}>Mark all ready</button>
              <button className="btn btn-ghost" onClick={() => signoff('open', false)}>Re-open all</button>
              {signMsg && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{signMsg}</span>}
            </div>}
          </div>
          {canEdit ? <RepoEdit repo={r} onSaved={load} /> : (
            <div className="card">
              <Kv label="Repo Jira card"><JiraLink jiraKey={r.jira_key} base={filters?.jira_base_url} /></Kv>
              <Kv label="Notes"><span style={{ whiteSpace: 'pre-wrap' }}>{r.notes || '—'}</span></Kv>
            </div>
          )}
        </div>

        {own && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ fontSize: 15 }}>Owner</h3>
              <span style={{ flex: 1 }} />
              <Link to={`/repo-owners?q=${encodeURIComponent(r.repo_slug)}`} style={{ fontSize: 12, marginRight: 10 }}>All repo owners</Link>
              {!editOwner && <button className="btn btn-ghost" onClick={() => setEditOwner(true)}>Edit owner</button>}
            </div>
            {editOwner ? (
              <OwnerEditForm slug={r.repo_slug} current={own} onCancel={() => setEditOwner(false)}
                onSaved={() => { setEditOwner(false); load(); }} />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
                <Kv label="Owner">{own.owner_name || '—'}<div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{own.owner_email || 'no email'}</div></Kv>
                <Kv label="Title">{own.owner_title || '—'}</Kv>
                <Kv label="Manager">{own.owner_manager || '—'}</Kv>
                <Kv label="How derived">{own.how_derived || '—'}</Kv>
                <Kv label="Flag"><span style={{ color: own.flag ? '#fbbf24' : undefined }}>{own.flag || '—'}</span></Kv>
                <Kv label="Source">{{ sheet: 'Ownership sheet', manual: `Set by hand${own.updated_by ? ` by ${own.updated_by}` : ''}`, scan: '23 Sep scan guess' }[own.source] || '—'}</Kv>
              </div>
            )}
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              <FolderOwnersPanel slug={r.repo_slug} trackerRepoId={r.id} onChanged={reload} />
            </div>
          </div>
        )}

        <KeyFilters get={get} set={set} filters={filters} hideRepo scopes={scopes} />
        {canEdit && (
          <BulkBar keyIds={[...selected]} total={keys.data.total} repoId={id} scopeId={get('scope_id') || scope || null}
            filterParams={{ ...Object.fromEntries(params), repo_id: id }}
            onDone={() => { setSelected(new Set()); reload(); }} onClear={() => setSelected(new Set())} />
        )}
        {keys.error && <div className="error-msg">{keys.error}</div>}
        <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>{keys.data.total} keys {keys.loading && <span className="spinner" />}</div>
        <KeysTable rows={keys.data.data} jiraBase={filters?.jira_base_url} selected={selected} setSelected={setSelected}
          repoId={id} onChanged={reload} />
        <Pager page={keys.data.page} limit={keys.data.limit} total={keys.data.total} onPage={(p) => set({ page: p }, { keepPage: true })} />

        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 15, marginBottom: 12 }}>Repo history</h3>
          <EventList events={d.events} />
        </div>
      </div>
    </div>
  );
}
